import axios from 'axios'

import useLocalStorage from './useLocalStorage'

import type { FormattedReward } from '@/types/reward'
import type { LoyaltyReward } from '@/types/types'
import {
  filterActiveRewards,
  getRewardStatus,
  getRewardTypeIcon,
  getRewardTypeLabel,
  getRewardUsageTags,
  normalizeLoyaltyRewardFromApi
} from '@/components/rewards/utils/rewardHelpers'

const isStoreSelectorDisplayed = ref(true)
const isVoucherSupported = ref(true)
const rewards = ref<LoyaltyReward[]>([])
const isLoading = ref(false)
const pagination = ref({
  currentPage: 1,
  lastPage: 1,
  total: 0,
  pageSize: 10
})

const normalizeReward = (reward: LoyaltyReward): LoyaltyReward => {
  normalizeLoyaltyRewardFromApi(reward)
  return reward
}

const normalizeRewards = (rows: LoyaltyReward[] | null | undefined): LoyaltyReward[] => {
  if (!Array.isArray(rows)) return []
  return rows.map((reward) => normalizeReward(reward))
}

export default function useReward() {
  const { t } = useI18n()
  const router = useRouter()
  const { user } = useLocalStorage()
  const notification = useNotification()

  const activeRewards = computed(() => filterActiveRewards(rewards.value))

  const hasRewards = computed(() => rewards.value.length > 0)

  const formattedRewards = computed(() => {
    return rewards.value.map((reward) => ({
      ...reward,
      typeIcon: getRewardTypeIcon(reward.rewardType),
      typeLabel: getRewardTypeLabel(reward.rewardType, t),
      status: getRewardStatus(reward, t),
      usageTags: getRewardUsageTags(reward.usages ?? [], t)
    })) as FormattedReward[]
  })

  const fetchMerchantRewards = async (
    storeId?: number,
    sortBy?: string,
    sortOrder?: string,
    page: number = 1,
    pageSize: number = 10,
    showAllRewards: boolean = false
  ): Promise<{ data: LoyaltyReward[]; meta: { current_page: number; last_page: number; total: number } }> => {
    try {
      let urlParams: {
        sortBy?: string
        sortOrder?: string
        storeId?: number
        page?: number
        pageSize?: number
        showAllRewards?: boolean
      } = {
        sortBy: sortBy || 'points',
        sortOrder: sortOrder || 'ASC',
        page,
        pageSize,
        showAllRewards
      }

      if (storeId) {
        urlParams = {
          ...urlParams,
          storeId
        }
      }

      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/rewards`, { params: urlParams })
      return {
        ...data,
        data: normalizeRewards(data.data as LoyaltyReward[])
      }
    } catch (error: unknown) {
      notification.create('error', {
        text: t('pages.reward.errors.fetchRewards')
      })
      throw error
    }
  }

  const fetchAllRewards = async () => {
    try {
      const response = await axios.get(`/merchants/${user.value.merchant.id}/rewards`, {
        params: {
          pageSize: 1000,
          showAllRewards: true
        }
      })

      return normalizeRewards(response.data.data as LoyaltyReward[])
    } catch {
      notification.create('error', {
        text: t('pages.reward.errors.fetchRewards')
      })
    }
  }

  const associateLoyaltyProgramReward = async (
    rewardId: number,
    params?: { notificationTextOverride?: string | null }
  ): Promise<LoyaltyReward> => {
    const { data } = await axios.post(
      `/merchants/${user.value.merchant.id}/loyalty-program/rewards/${rewardId}/associate`,
      params ?? {}
    )
    return normalizeReward((data.data?.reward ?? data.data) as LoyaltyReward)
  }

  const fetchLoyaltyProgramRewardsPaged = async (
    storeIds?: number[],
    sortBy?: string,
    sortOrder?: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{ data: LoyaltyReward[]; meta: { current_page: number; last_page: number; total: number } }> => {
    try {
      let params: {
        sortBy?: string
        sortOrder?: string
        store_ids?: number[]
        page?: number
        pageSize?: number
      } = {
        sortBy: sortBy || 'points',
        sortOrder: sortOrder || 'ASC',
        page,
        pageSize
      }

      if (storeIds && storeIds.length > 0) {
        params = { ...params, store_ids: storeIds }
      }

      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/loyalty-program/rewards`, {
        params
      })
      return {
        ...data,
        data: normalizeRewards(data.data as LoyaltyReward[])
      }
    } catch (error: unknown) {
      notification.create('error', {
        text: t('pages.reward.errors.fetchRewards')
      })
      throw error
    }
  }

  const loadRewards = async (storeId?: number, sortBy?: string, sortOrder?: string, page: number = 1) => {
    try {
      isLoading.value = true
      const response = await fetchMerchantRewards(storeId, sortBy, sortOrder, page, pagination.value.pageSize, true)
      rewards.value = response.data || []

      pagination.value = {
        currentPage: response.meta.current_page,
        lastPage: response.meta.last_page,
        total: response.meta.total,
        pageSize: pagination.value.pageSize
      }
    } catch {
      rewards.value = []
      pagination.value = {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        pageSize: pagination.value.pageSize
      }
    } finally {
      isLoading.value = false
    }
  }

  const changePage = async (page: number) => {
    await loadRewards(
      undefined, // storeId - will be handled by the calling component
      undefined, // sortBy - will be handled by the calling component
      undefined, // sortOrder - will be handled by the calling component
      page
    )
  }

  const navigateToRewardDetail = (reward: LoyaltyReward) => {
    router.push(`/rewards/${reward.id}`)
  }

  return {
    isStoreSelectorDisplayed,
    isVoucherSupported,
    rewards,
    isLoading,
    hasRewards,
    activeRewards,
    formattedRewards,
    pagination,
    fetchMerchantRewards,
    fetchAllRewards,
    fetchLoyaltyProgramRewardsPaged,
    associateLoyaltyProgramReward,
    loadRewards,
    changePage,
    navigateToRewardDetail
  }
}
